import type { AssistantMessage, ChatMessage, ToolCall, ToolSubstep } from '../types'
import { isSynthContinuationId } from '../utils/synth-continuation'

function contentKey(message: ChatMessage): string {
  return typeof message.content === 'string' ? message.content : ''
}

function contentLength(message: ChatMessage): number {
  return contentKey(message).length
}

/**
 * The snapshot's copy of the in-flight send, or undefined when it holds none.
 * The send is the conversation's newest message, so only the snapshot's last
 * user message can be its copy, and only when local has no id for it: the
 * local bubble still carries its pre-rename UUID. Matching any historical
 * user message would false-drop a send that repeats earlier text like "yes"
 * or "shorter".
 */
function persistedSend(
  fetched: Array<ChatMessage>,
  existing: Array<ChatMessage>,
): { index: number; content: string } | undefined {
  const localIds = new Set(existing.map((m) => m.id))
  for (let i = fetched.length - 1; i >= 0; i--) {
    const message = fetched[i]
    if (message.role !== 'user') continue
    return localIds.has(message.id) ? undefined : { index: i, content: contentKey(message) }
  }
  return undefined
}

const TOOL_STATUS_RANK: Record<ToolCall['status'], number> = {
  ready: 0,
  executing: 1,
  success: 2,
  failed: 2,
  cancelled: 2,
}

const STEP_STATUS_RANK: Record<ToolSubstep['stepStatus'], number> = {
  started: 0,
  streaming: 1,
  completed: 2,
}

/**
 * Whether the local tool call has execution progress its fetched counterpart
 * lacks: a further-along status, or — at the same status — more steps or
 * subagents, or a step whose own status has advanced. Counts alone miss
 * same-shaped snapshots where only the statuses moved (ready → executing →
 * success). An unequal lifecycle status decides outright: sub-progress like
 * steps (which the backend may not persist at all) must never keep a local
 * `executing` spinner alive over a snapshot that already has the terminal
 * result.
 */
function toolCallIsAhead(local: ToolCall, fetched: ToolCall | undefined): boolean {
  if (!fetched) return true
  const localRank = TOOL_STATUS_RANK[local.status]
  const fetchedRank = TOOL_STATUS_RANK[fetched.status]
  if (localRank !== fetchedRank) return localRank > fetchedRank
  if ((local.steps?.length ?? 0) > (fetched.steps?.length ?? 0)) return true
  if ((local.subagents?.length ?? 0) > (fetched.subagents?.length ?? 0)) return true
  const fetchedSteps = new Map((fetched.steps ?? []).map((step) => [step.stepId, step]))
  return (local.steps ?? []).some((step) => {
    const counterpart = fetchedSteps.get(step.stepId)
    return counterpart !== undefined && STEP_STATUS_RANK[step.stepStatus] > STEP_STATUS_RANK[counterpart.stepStatus]
  })
}

/**
 * Whether the local assistant fold has stream progress the backend snapshot
 * lacks: more visible text, content parts, or folded reasoning; a still-live
 * thinking stream; or any tool call whose execution is further along. Any of
 * these means the refetch is stale for this message.
 */
function localFoldIsAhead(local: AssistantMessage, fetched: AssistantMessage): boolean {
  if (contentLength(local) > contentLength(fetched)) return true
  if ((local.contentParts?.length ?? 0) > (fetched.contentParts?.length ?? 0)) return true
  if ((local.thinkingContent?.length ?? 0) > (fetched.thinkingContent?.length ?? 0)) return true
  if (local.isThinkingStreaming === true && fetched.isThinkingStreaming !== true) return true
  const fetchedToolCalls = new Map((fetched.toolCalls ?? []).map((toolCall) => [toolCall.id, toolCall]))
  return (local.toolCalls ?? []).some((toolCall) => toolCallIsAhead(toolCall, fetchedToolCalls.get(toolCall.id)))
}

/**
 * Merge a refetched message history with the atom's current messages.
 *
 * Backend-ahead refetches replace the atom (an initial load, or a refetch
 * that caught up) — but never wholesale (HIG-5971): a refetch landing during
 * an active stream used to swap every message identity and revert in-flight
 * streamed text to the backend's stale snapshot. Per message:
 *
 * - A local assistant message that is ahead of its fetched counterpart while
 *   the chat streams (text, tool calls, content parts, or reasoning the
 *   backend hasn't persisted) keeps the local object, preserving content and
 *   identity. This is per-id and independent of how many messages each side
 *   has — extra local rows (the placeholder, an optimistic user message, an
 *   unpersisted tail) must not decide whether a live fold survives.
 * - The unadopted assistant placeholder (`id === undefined`) is always kept.
 * - A local-only message that sits *before* a message the snapshot knows is
 *   never in flight: the backend persists in order, so a row it has already
 *   moved past is a stale duplicate — typically the optimistic user bubble
 *   whose client id was never swapped for the backend id — and is dropped.
 *   Keeping it would re-attach it below every fetched message, i.e. the
 *   user's message under the reply it produced, and the next turn's refetch
 *   would then drag the *previous* message to the bottom (HIG-6215).
 * - A local-only user message whose persisted copy the snapshot already
 *   holds (same content as the snapshot's newest user message, which local
 *   has no id for) is dropped in favor of that copy, whatever the counts —
 *   a refetch landing after the send persisted but before the reply did
 *   used to keep both. The local clientId moves onto the copy so the
 *   bubble's group key survives.
 * - Otherwise local-only messages the backend doesn't know yet (minus
 *   synthetic continuations) are appended when either the backend is behind
 *   on message count — it hasn't caught up with a post-edit truncation or an
 *   unpersisted stream — or the chat is streaming and the message is an
 *   assistant.
 * - A local-only *user* message during a live turn is the turn's own
 *   optimistic bubble. An initial history load can beat the backend
 *   persisting the send, and dropping the bubble deletes the user's message
 *   and lets the reply group under the previous assistant bubble. A
 *   staged-action edit hits this exact race: submitting opens the chat
 *   drawer, whose cold fetch runs while the POST is in flight.
 * - Outside a live turn, local-only user messages are dropped: their random
 *   UUID may not have been swapped for the backend id yet, so keeping them
 *   would duplicate the sent message.
 */
export function mergeRefetchedMessages(
  fetched: Array<ChatMessage>,
  existing: Array<ChatMessage>,
  isStreaming: boolean,
): Array<ChatMessage> {
  const existingById = new Map(existing.filter((m) => m.id !== undefined).map((m) => [m.id, m]))
  const merged = fetched.map((message) => {
    if (!isStreaming || message.role !== 'assistant') return message
    const local = existingById.get(message.id)
    if (!local || local.role !== 'assistant') return message
    // The local fold is ahead of the backend snapshot — keep it.
    return localFoldIsAhead(local as AssistantMessage, message as AssistantMessage) ? local : message
  })

  // Fewer fetched than local means the backend hasn't caught up with what the
  // atom holds, so every local-only message is in-flight content, not a stale
  // pre-rename duplicate.
  const backendIsBehind = fetched.length < existing.length
  const fetchedIds = new Set(fetched.map((m) => m.id))
  const persisted = persistedSend(fetched, existing)
  // Everything local-only before this index has been overtaken by the backend.
  let lastKnownIndex = -1
  for (let i = existing.length - 1; i >= 0; i--) {
    const { id } = existing[i]
    if (id !== undefined && fetchedIds.has(id)) {
      lastKnownIndex = i
      break
    }
  }
  let persistedClientId: string | undefined
  const inFlight = existing.filter((m, index) => {
    // id === undefined is the unadopted assistant placeholder — always in-flight, always kept
    if (m.id === undefined) return true
    if (fetchedIds.has(m.id) || isSynthContinuationId(m.id)) return false
    if (index < lastKnownIndex) return false
    if (m.role === 'user' && persisted !== undefined && contentKey(m) === persisted.content) {
      persistedClientId ??= m.clientId
      return false
    }
    if (backendIsBehind) return true
    if (!isStreaming) return false
    if (m.role === 'assistant') return true
    // The live turn's optimistic user message: client-minted, not yet in the
    // snapshot under any id.
    return m.role === 'user' && m.clientId !== undefined
  })

  if (persisted !== undefined && persistedClientId !== undefined && merged[persisted.index].clientId === undefined) {
    merged[persisted.index] = { ...merged[persisted.index], clientId: persistedClientId }
  }

  return [...merged, ...inFlight]
}
